// Runtime feature gating.
//
// Staging and production are both Vite "production" builds (import.meta.env.PROD is true for both),
// so build-time flags can't distinguish them. We gate by the actual host at runtime instead, which
// is robust regardless of how each image is built/deployed.

import { isNativePlatform } from './platform';

// Hosts where in-progress features may be shown. Production (app.daconnect.com.au,
// app.data-scraping.com, …) is deliberately excluded.
const STAGING_HOSTS = ['test-app.daconnect.com.au'];

/**
 * True on the staging site or local development, false on production and in the native app
 * (which is production-facing). Use to expose features that aren't ready for production yet.
 */
export function isStagingOrDev(): boolean {
    // The native app points at the production API — never treat it as staging.
    if (isNativePlatform()) return false;

    // Vite dev server (npm run dev).
    if (import.meta.env.DEV) return true;

    if (typeof window === 'undefined') return false;

    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return true;

    return STAGING_HOSTS.includes(host);
}

/**
 * The marketplace — the public site at "/", the listing editor, the enquiries inbox and featured
 * placements.
 *
 * <p>OPEN EVERYWHERE. It was staging-only while it was being built; it is finished, and the server's
 * Features:Marketplace:Enabled is on to match. The native app gets it too — it points at the
 * production API, so it sees the same product as the web.</p>
 */
export function isMarketplaceEnabled(): boolean {
    return true;
}

/**
 * The opportunity feed — STILL A PROTOTYPE, and still staging and local dev only.
 *
 * <p>Deliberately left closed while the marketplace, the redesign and subscriptions were opened.
 * ContactComposer says it plainly: "this prototype does not send", "This drawer sends nothing". A
 * customer finding a compose window that silently drops what they write is worse than not finding
 * the feature at all.</p>
 *
 * <p>The server enforces the same gate via Features:OpportunityFeed:Enabled, and refuses those
 * endpoints in production regardless; this only hides the UI.</p>
 */
export function isOpportunityFeedEnabled(): boolean {
    return isStagingOrDev();
}

/**
 * Whether anyone can START a subscription (Stripe Checkout, the subscribe/resubscribe forms).
 *
 * This was a constant `false` while there was no flat-fee price to charge, then a staged rollout
 * while the prices existed only in the sandbox. Both reasons are gone: the live price ids are in the
 * server's appsettings.Secrets.json, the server's Features:Subscriptions:Enabled is on, and the
 * webhook carries checkout.session.completed.
 *
 * <p><b>THIS CHARGES REAL CARDS.</b> With it on, Checkout, plan changes, seat purchases and featured
 * weeks all bill against the live Stripe account. It is not a display flag.</p>
 *
 * This only hides UI. The server enforces the same closure via Features:Subscriptions:Enabled
 * (SubscriptionsFeatureSettings), which 404s checkout and subscription creation — note that GET
 * /api/stripe/plans is NOT gated, so prices stay readable either way. Neither gate touches cancel,
 * reactivate, the billing portal, the webhooks or any read path — an existing subscriber must
 * still be able to see and cancel what they are paying for.
 */
export function isSubscribeEnabled(): boolean {
    return true;
}

/**
 * The redesigned app shell — the new sidebar, tokens and screens.
 *
 * <p>OPEN EVERYWHERE, including the native app. This is the whole signed-in experience: AppLayout
 * hands over to RedesignLayout, so every customer sees it on their next load. There is no per-user
 * opt-out and no way back for an individual — the only lever is this function.</p>
 *
 * <p>Still its own flag rather than folded into isMarketplaceEnabled(): keeping them apart is what
 * let the marketplace and the shell be turned on independently, and would let one be pulled back
 * without the other.</p>
 */
export function isAppRedesignEnabled(): boolean {
    return true;
}
